import { BlockSelectField } from '@common/components/FormElements';
import { DaySelector } from '@common/components/DaySelector';
import { useIsDesktop } from '@common/hooks/useIsDesktop';
import { useTranslations } from '@lang';
import { TimeSlotOptionHOC } from '@features/spa/pages/SpaDay/SpaPackageModal/SpaPackageAvailability/DateTimeSelector/TimeslotSelector/TimeSlotOptionHOC';
import { some } from '@utils/some';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Controller, Form, useFormContext } from 'react-hook-form';
import { AvailabilitySlot } from 'shared-types';

import { isOverlapping } from '../../helpers/isTimeslotOverlapping';
import { usePackageConfiguration } from '../../contexts';
import { useGuestExperiences } from '../../FormSteps/hooks/useGuestExperiences';

import {
  PackagesFormValues,
  ExperienceType,
  AvailabilityForm as AvailabilityFormType,
} from '../../types';
import { TimeSlotType } from 'shared-types/SpaPackage';
import { getSelectedTimeslotsWithStaffAndRoomAllocation } from '../../helpers/getSelectedTimeslotsWithStaffAndRoomAllocation';
import { hasStaffAndRoomAvailability } from '../../helpers/hasStaffAndRoomAvailability';
import { AvailabilityFormWrapper, DayContainer, SelectWrapper } from './AvailabilityForm.styles';
import { Label } from '@common/components/form/Label';
import { SpaLabel } from '../../FormSteps/ExperiencesStep/MultiDayExperiencesStep/MultiDayExperiencesStep.styles';

const TimeSlotOption = (props) => <TimeSlotOptionHOC parentType="Option" {...props} />;
const TimeSlotValue = (props) => <TimeSlotOptionHOC parentType="SingleValue" {...props} />;

export interface AvailabilityFormProps {
  timeslots: TimeSlotType[];
  namePrefix: string;
  id?: number;
  entityType: ExperienceType;
  guestIndex: number;
  delayAfter?: number;
  dayNumber?: number;
  dayNumberRange?: number[];
  experienceIndex: number;
  minimumTimeslotQuantity?: number;
  showDaySelector?: boolean;
  selectedDayFromParent?: number;
}

type TimeslotOption = AvailabilitySlot & {
  label: string;
  value: Date;
  experienceId: number;
  type: ExperienceType;
  delayAfter: number;
  optionGroupId?: number | string;
  optionId: number | string;
  startTime: string;
  endTime: string;
  visitDate: string;
  quantity: number | null;
  staff?: string[];
  rooms?: string[];
};

export type ArrivalTimeOptionType = TimeslotOption & { dayNumber: number };

export const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
  timeslots,
  id,
  entityType,
  guestIndex,
  delayAfter,
  dayNumber,
  dayNumberRange = [],
  experienceIndex,
  minimumTimeslotQuantity = 1,
  showDaySelector = false,
  namePrefix,
  selectedDayFromParent,
}) => {
  const { t } = useTranslations();
  const isDesktop = useIsDesktop();
  const { journeyParams, reservedSpaTimeSlots, stayDuration } = usePackageConfiguration();
  const arrivalTimeName = namePrefix as keyof PackagesFormValues;
  const form = useFormContext<PackagesFormValues>();
  const { control, setValue, watch, getValues } = form;

  const isSameTimeForAllGuests = minimumTimeslotQuantity > 1;

  const selectedDayNumber = watch(`${arrivalTimeName}.dayNumber` as keyof PackagesFormValues);

  const activeDayNumber = useMemo(() => {
    if (selectedDayFromParent !== undefined) {
      return selectedDayFromParent;
    }
    if (selectedDayNumber !== undefined && selectedDayNumber !== null) {
      return Number(selectedDayNumber);
    }
    return dayNumber;
  }, [selectedDayFromParent, selectedDayNumber, dayNumber]);

  const experienceDate = useMemo(() => {
    return moment(journeyParams.fromDate).add(activeDayNumber, 'days').format('YYYY-MM-DD');
  }, [activeDayNumber, journeyParams.fromDate]);

  const allStayDates = useMemo(() => {
    return Array.from({ length: stayDuration }, (_, i) =>
      moment(journeyParams.fromDate).add(i, 'days')
    );
  }, [journeyParams.fromDate, stayDuration]);

  const availableDates = useMemo(() => {
    return dayNumberRange.map((dayNum) => moment(journeyParams.fromDate).add(dayNum, 'days'));
  }, [dayNumberRange, journeyParams.fromDate]);

  const handleDayChange = (dateString: string) => {
    const selectedDate = moment(dateString);
    const dayNum = selectedDate.diff(moment(journeyParams.fromDate), 'days');
    const currentBooking = watch(arrivalTimeName) as AvailabilityFormType;

    const newBooking: Partial<AvailabilityFormType> = {
      ...currentBooking,
      dayNumber: dayNum,
      startTime: undefined,
      endTime: undefined,
      visitDate: undefined,
    };

    setValue(arrivalTimeName as any, newBooking, { shouldValidate: false, shouldDirty: true });
  };

  const arrivalTimeNameValue = watch(arrivalTimeName) as AvailabilityFormType;

  const allGuestExperiences = useGuestExperiences();
  const selectedExperiences = watch('selectedExperiences');
  const selectedTimeslot = watch(arrivalTimeName) as AvailabilityFormType;

  const guestExistingTimeslots = allGuestExperiences?.[guestIndex]?.bookings?.filter(
    (slot) =>
      slot &&
      slot.experienceId !== arrivalTimeNameValue?.experienceId &&
      slot.dayNumber === activeDayNumber
  );

  const experienceExistingTimeslots = allGuestExperiences
    ?.filter((_, index) => index !== guestIndex)
    .flatMap(
      (guest) =>
        guest.bookings?.filter(
          (ts) =>
            ts &&
            ts.startTime &&
            ts.endTime &&
            ts.experienceId === id &&
            ts.dayNumber === activeDayNumber
        ) || []
    );

  const selectedTimeslotsWithStaffAndRoomAllocation =
    getSelectedTimeslotsWithStaffAndRoomAllocation(allGuestExperiences);

  const availableTimeOptions =
    (timeslots?.length > 0 &&
      timeslots.filter((time) => !reservedSpaTimeSlots.includes(time?.startTime.toString()))) ||
    [];

  const arrivalTimesOptions = availableTimeOptions.reduce(
    (acc: Array<TimeslotOption & { dayNumber: number }>, timeslot) => {
      const experience = (selectedExperiences || []).find((exp) => Number(exp.id) === Number(id));

      if (experience && id && experienceDate && activeDayNumber !== undefined) {
        acc.push({
          ...timeslot,
          label: timeslot.startTime,
          delayAfter: delayAfter || 0,
          value: new Date(`${experienceDate} ${timeslot.startTime}`),
          experienceId: id,
          type: entityType,
          optionId: experience.optionId,
          optionGroupId: experience.optionGroupId,
          startDateTime: new Date(timeslot.startTime),
          endDateTime: new Date(timeslot.endTime),
          bandNumber: 0,
          discount: undefined,
          dayNumber: activeDayNumber,
          visitDate: experienceDate,
          staff: timeslot.staff,
          rooms: timeslot.rooms,
        });
      }
      return acc;
    },
    []
  );

  const hasSameGuestForTimeslot = (timeslot: ArrivalTimeOptionType) =>
    !some(
      guestExistingTimeslots,
      (existingTimeslot) =>
        existingTimeslot &&
        timeslot.type !== 'spa-facilities' &&
        existingTimeslot.type !== 'spa-facilities' &&
        isOverlapping(timeslot, existingTimeslot)
    );

  const hasQuantityAndTimeslotAvailable = (timeslot: ArrivalTimeOptionType) => {
    if (timeslot.quantity !== null && timeslot.quantity < minimumTimeslotQuantity) {
      return false;
    }

    const [timeslotStartHour, timeslotStartMinute] = timeslot.startTime.split(':');

    return !some(experienceExistingTimeslots, (existingTimeslot) => {
      const [existingTimeslotStartHour, existingTimeslotStartMinute] = (
        existingTimeslot?.startTime || ''
      ).split(':');

      const isStillAvailableAfterUserSelection =
        timeslot.quantity && timeslot?.quantity <= experienceExistingTimeslots.length;

      const isAtTheSameTimeAsUserSelection = moment(experienceDate)
        .set({
          hour: Number(existingTimeslotStartHour),
          minute: Number(existingTimeslotStartMinute),
        })
        .isSame(
          moment(experienceDate).set({
            hour: Number(timeslotStartHour),
            minute: Number(timeslotStartMinute),
          })
        );
      return isStillAvailableAfterUserSelection && isAtTheSameTimeAsUserSelection;
    });
  };

  const hasAvailableResources = (timeslot: ArrivalTimeOptionType) => {
    if (timeslot.staff === undefined && timeslot.rooms === undefined) {
      return true;
    }
    return hasStaffAndRoomAvailability(
      timeslot,
      selectedTimeslotsWithStaffAndRoomAllocation,
      guestIndex
    );
  };

  const filteredArrivalTimesOptions = arrivalTimesOptions.filter((timeslot) => {
    return (
      hasSameGuestForTimeslot(timeslot) &&
      hasQuantityAndTimeslotAvailable(timeslot) &&
      hasAvailableResources(timeslot)
    );
  });

  const mappedArrivalTimes = filteredArrivalTimesOptions.map((ts) => {
    const { price: _price, startTime, ...rest } = ts;
    const [hours, minutes] = startTime.split(':');
    return { ...rest, startTime, label: `${hours}:${minutes}` };
  });

  useEffect(() => {
    if (!selectedTimeslot) {
      if (isSameTimeForAllGuests) {
        const guests = getValues('guests') || [];
        guests.forEach((_, idx) => {
          setValue(
            `guests.${idx}.experiences.${experienceIndex}`,
            null as unknown as AvailabilityFormType
          );
        });
      } else {
        setValue(
          `guests.${guestIndex}.experiences.${experienceIndex}`,
          null as unknown as AvailabilityFormType
        );
      }
    } else if (selectedDayNumber !== undefined && selectedDayNumber !== null) {
      if (isSameTimeForAllGuests) {
        const guests = getValues('guests') || [];
        guests.forEach((_, idx) => {
          setValue(
            `guests.${idx}.experiences.${experienceIndex}.dayNumber`,
            Number(selectedDayNumber)
          );
        });
      } else {
        setValue(
          `guests.${guestIndex}.experiences.${experienceIndex}.dayNumber`,
          Number(selectedDayNumber)
        );
      }
    }
  }, [
    selectedTimeslot,
    guestIndex,
    experienceIndex,
    selectedDayNumber,
    setValue,
    isSameTimeForAllGuests,
    getValues,
  ]);

  const value = selectedTimeslot?.startTime
    ? mappedArrivalTimes.find((opt) => opt?.startTime === selectedTimeslot.startTime)
    : null;

  return (
    <Form>
      <AvailabilityFormWrapper>
        {showDaySelector && (
          <>
            <SpaLabel required>{t('spa.selectDate')}</SpaLabel>
            <DayContainer>
              <DaySelector
                availableDates={availableDates}
                startDate={allStayDates[0]}
                endDate={allStayDates[allStayDates.length - 1]}
                currentSelection={
                  selectedDayNumber !== undefined && selectedDayNumber !== null
                    ? experienceDate
                    : undefined
                }
                onChange={handleDayChange}
              />
            </DayContainer>
          </>
        )}
        {activeDayNumber !== undefined && activeDayNumber !== null && (
          <SelectWrapper>
            <Label required>{t('aria.optionSelectorDropdownPlaceholder')}</Label>
            <Controller
              control={control}
              name={arrivalTimeName as keyof PackagesFormValues}
              render={({ field: { onChange } }) => {
                return (
                  <BlockSelectField
                    components={{
                      Option: TimeSlotOption,
                      SingleValue: TimeSlotValue,
                    }}
                    options={mappedArrivalTimes}
                    onChange={(val) => {
                      const selectedTime = arrivalTimesOptions.find(
                        (opt) => opt.value === val.value
                      );
                      if (selectedTime) {
                        const updatedTime = {
                          ...selectedTime,
                          guestIndex,
                        };

                        if (isSameTimeForAllGuests) {
                          const guests = getValues('guests') || [];
                          guests.forEach((_, idx) => {
                            setValue(`guests.${idx}.experiences.${experienceIndex}`, {
                              ...updatedTime,
                              optionId: Number(updatedTime.optionId),
                              optionGroupId: updatedTime.optionGroupId ?? '',
                            });
                          });
                        }

                        return onChange(updatedTime);
                      }
                    }}
                    isDisabled={!id}
                    value={value}
                    placeholder={t('aria.optionSelectorDropdownPlaceholder')}
                    useNativeDropdownOnMobile={false}
                    isSearchable={false}
                    menuPlacement={isDesktop ? 'auto' : 'top'}
                    noOptionsMessage={() => t('treatment.noTimeslotsAvailable')}
                  />
                );
              }}
            />
          </SelectWrapper>
        )}
      </AvailabilityFormWrapper>
    </Form>
  );
};
